import React from 'react';
import './LoadingSpinner.css';

/**
 * LoadingSpinner component with ArbiWin design system integration
 * Supports multiple sizes and color variants
 *
 * @param {Object} props - LoadingSpinner properties
 * @param {string} props.size - Spinner size: 'small', 'medium', 'large', 'xlarge'
 * @param {string} props.variant - Spinner variant: 'primary', 'secondary', 'white'
 * @param {string} props.label - Accessible label for screen readers
 * @param {boolean} props.inline - Display inline instead of centered
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} LoadingSpinner component
 */
const LoadingSpinner = function LoadingSpinner({
  size = 'medium',
  variant = 'primary',
  label = 'Зареждане...',
  inline = false,
  className = '',
  testId
}) {

  const spinnerClasses = [
    'arbiwin-spinner',
    'arbiwin-spinner--' + size,
    'arbiwin-spinner--' + variant,
    inline ? 'arbiwin-spinner--inline' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      className={spinnerClasses}
      role="status"
      aria-label={label}
      aria-live="polite"
      data-testid={testId || 'arbiwin-spinner'}
    >
      <svg
        className="arbiwin-spinner__svg"
        viewBox="0 0 50 50"
        aria-hidden="true"
      >
        <circle
          className="arbiwin-spinner__track"
          cx="25"
          cy="25"
          r="20"
          fill="none"
          strokeWidth="4"
        />
        <circle
          className="arbiwin-spinner__fill"
          cx="25"
          cy="25"
          r="20"
          fill="none"
          strokeWidth="4"
          strokeDasharray="125.66"
          strokeDashoffset="125.66"
          strokeLinecap="round"
        />
      </svg>
      <span className="arbiwin-spinner__sr-only">
        {label}
      </span>
    </div>
  );
};

/**
 * LoadingDots component - Alternative loading indicator
 *
 * @param {Object} props - LoadingDots properties
 * @param {string} props.size - Dots size: 'small', 'medium', 'large'
 * @param {string} props.variant - Dots variant: 'primary', 'secondary', 'white'
 * @param {string} props.label - Accessible label for screen readers
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} LoadingDots component
 */
const LoadingDots = function LoadingDots({
  size = 'medium',
  variant = 'primary',
  label = 'Зареждане...',
  className = '',
  testId
}) {

  const dotsClasses = [
    'arbiwin-loading-dots',
    'arbiwin-loading-dots--' + size,
    'arbiwin-loading-dots--' + variant,
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      className={dotsClasses}
      role="status"
      aria-label={label}
      aria-live="polite"
      data-testid={testId || 'arbiwin-loading-dots'}
    >
      <div className="arbiwin-loading-dots__dot" aria-hidden="true" />
      <div className="arbiwin-loading-dots__dot" aria-hidden="true" />
      <div className="arbiwin-loading-dots__dot" aria-hidden="true" />
      <span className="arbiwin-loading-dots__sr-only">
        {label}
      </span>
    </div>
  );
};

/**
 * LoadingSkeleton component - For content placeholders
 *
 * @param {Object} props - LoadingSkeleton properties
 * @param {string} props.variant - Skeleton variant: 'text', 'circular', 'rectangular'
 * @param {string} props.width - Skeleton width
 * @param {string} props.height - Skeleton height
 * @param {number} props.lines - Number of text lines (for text variant)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} LoadingSkeleton component
 */
const LoadingSkeleton = function LoadingSkeleton({
  variant = 'text',
  width,
  height,
  lines = 1,
  className = '',
  testId
}) {

  const skeletonClasses = [
    'arbiwin-skeleton',
    'arbiwin-skeleton--' + variant,
    className
  ].filter(Boolean).join(' ');

  const skeletonStyle = {};
  if (width) skeletonStyle.width = width;
  if (height) skeletonStyle.height = height;

  if (variant === 'text' && lines > 1) {
    return (
      <div
        className="arbiwin-skeleton-group"
        data-testid={testId || 'arbiwin-skeleton-group'}
        aria-hidden="true"
      >
        {Array.from({ length: lines }, function(_, index) {
          const isLast = index === lines - 1;
          const lineStyle = Object.assign({}, skeletonStyle);
          if (isLast && !width) {
            lineStyle.width = '75%'; // Last line is typically shorter
          }

          return (
            <div
              key={index}
              className={skeletonClasses}
              style={lineStyle}
              aria-hidden="true"
            />
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={skeletonClasses}
      style={skeletonStyle}
      data-testid={testId || 'arbiwin-skeleton'}
      aria-hidden="true"
    />
  );
};

LoadingSpinner.displayName = 'LoadingSpinner';
LoadingDots.displayName = 'LoadingDots';
LoadingSkeleton.displayName = 'LoadingSkeleton';

// Export all loading components
export default LoadingSpinner;
export { LoadingDots, LoadingSkeleton };
